import { computed, reactive, ref } from 'vue'
import { useStore } from 'vuex'

import { useSettingsAccess } from '@/composables/useSettingsAccess'
import privilegeService from '@/services/privilege.service'
import api from '@/store/api'
import { mapRelationships } from '@/store/mappers'

export const OFFICE_HOUR_DAYS = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' }
]

const EDITABLE = ['handle', 'email', 'time_zone', 'voicemail_enabled', 'read_voicemail_message']
const OFFICE_HOUR_FIELDS = OFFICE_HOUR_DAYS.flatMap(day => [
  `${day.key}_office_hours_start`,
  `${day.key}_office_hours_end`
])
const ARCHIVE_KEYS = new Set(['autoArchiveOnOptOut', 'autoArchiveInactiveDays'])

// One form behind every settings area. The profile PUT sends the whole of `values`,
// so a page that loaded only its own slice would blank every field it never read -
// the areas share this state rather than each owning a piece of it.
const values = reactive({
  ...Object.fromEntries([...EDITABLE, ...OFFICE_HOUR_FIELDS].map(key => [key, undefined])),
  paytronixGroup: undefined,
  paytronixStore: undefined,
  systemUserEmail: undefined,
  systemUserFullName: undefined,
  autoArchiveOnOptOut: false,
  autoArchiveInactiveDays: null
})
const editing = reactive({
  ...Object.fromEntries([...EDITABLE, ...OFFICE_HOUR_FIELDS].map(key => [key, false])),
  paytronixGroup: false,
  paytronixStore: false,
  systemUserEmail: false,
  systemUserFullName: false,
  autoArchiveOnOptOut: false,
  autoArchiveInactiveDays: false,
  favicon: false
})
const officeHoursClosed = reactive(Object.fromEntries(OFFICE_HOUR_DAYS.map(day => [day.key, false])))
const officeHoursPrevious = reactive(Object.fromEntries(OFFICE_HOUR_DAYS.map(day => [day.key, { start: '', end: '' }])))
const faviconUpload = ref(null)
const faviconPreview = ref(null)
const financialEnabled = ref(false)
const loadedFor = ref(null)

export function resetSettingsForm() {
  loadedFor.value = null
}

export function useSettingsForm() {
  const store = useStore()
  const { user, hasFullAccess, systemUserId } = useSettingsAccess()

  const integrations = computed(() =>
    user.value?.relationships?.integrations ? mapRelationships(user.value.relationships.integrations.data) : []
  )
  const paytronix = computed(() => integrations.value.find(x => x.attributes.service === 'paytronix'))

  const hasUnsavedChanges = computed(() => Object.values(editing).some(Boolean))
  const autoArchiveInactiveEnabled = computed(() => values.autoArchiveInactiveDays !== null)
  const archiveInactiveDaysValid = computed(() => {
    if (values.autoArchiveInactiveDays === null) return true
    const days = values.autoArchiveInactiveDays
    return Number.isInteger(days) && days >= 30 && days <= 730
  })

  const syncOfficeHoursClosedFromValues = () => {
    OFFICE_HOUR_DAYS.forEach(day => {
      const closed =
        values[`${day.key}_office_hours_start`] === 'closed' && values[`${day.key}_office_hours_end`] === 'closed'
      officeHoursClosed[day.key] = closed
      if (closed) officeHoursPrevious[day.key] = { start: '', end: '' }
    })
  }

  const readSystemUser = () => {
    values.systemUserFullName = store.state.objects?.system_user?.[systemUserId.value]?.attributes?.full_name ?? ''
    values.systemUserEmail =
      store.state.objects?.system_user?.[systemUserId.value]?.attributes?.email ??
      privilegeService.getSystemUserEmail() ??
      ''
  }

  const readProfile = () => {
    const attributes = store.state.loggedInUser.attributes
    for (const key of EDITABLE) values[key] = attributes[key]
    for (const field of OFFICE_HOUR_FIELDS) values[field] = attributes[field]
    syncOfficeHoursClosedFromValues()
    if (paytronix.value) {
      values.paytronixGroup = paytronix.value.attributes.attribute_one
      values.paytronixStore = paytronix.value.attributes.attribute_two
    }
    faviconPreview.value = attributes.favicon_url
    financialEnabled.value = attributes.hide_financial_data
    if (attributes.setting) {
      values.autoArchiveOnOptOut = !!attributes.setting.auto_archive_on_opt_out
      values.autoArchiveInactiveDays = attributes.setting.auto_archive_inactive_days
    }
    readSystemUser()
  }

  const load = async () => {
    const userId = store.getters.loggedInUserId
    if (loadedFor.value === userId) return
    loadedFor.value = userId
    await Promise.all([api.call('GET', 'profile?included=integrations'), api.call('GET', `users/${userId}`)])
    if (systemUserId.value) await api.call('GET', `system_users/${systemUserId.value}`)
    readProfile()
  }

  const onOfficeHoursChange = (field, value) => {
    values[field] = value
    editing[field] = true
  }

  const toggleOfficeClosed = (dayKey, isClosed) => {
    officeHoursClosed[dayKey] = isClosed
    const startKey = `${dayKey}_office_hours_start`
    const endKey = `${dayKey}_office_hours_end`
    if (isClosed) {
      officeHoursPrevious[dayKey] = { start: values[startKey], end: values[endKey] }
      values[startKey] = 'closed'
      values[endKey] = 'closed'
    } else {
      const previous = officeHoursPrevious[dayKey] || { start: '', end: '' }
      values[startKey] = previous.start || ''
      values[endKey] = previous.end || ''
    }
    editing[startKey] = true
    editing[endKey] = true
  }

  const onInactiveToggle = value => {
    values.autoArchiveInactiveDays = value ? 90 : null
    editing.autoArchiveInactiveDays = true
  }

  const onFaviconChange = event => {
    faviconUpload.value = event
    if (event?._destroy) faviconPreview.value = null
    else faviconPreview.value = event?._id ? store.state.objects?.attachment?.[event._id]?.attributes?.secure_url : null
  }

  const onFaviconRemove = () => {
    faviconUpload.value = { _destroy: true }
    faviconPreview.value = null
  }

  const handleSubmit = async () => {
    if ((editing.systemUserFullName || editing.systemUserEmail) && systemUserId.value) {
      const response = await api.call('PATCH', `system_users/${systemUserId.value}`, {
        system_user: { full_name: values.systemUserFullName, email: values.systemUserEmail }
      })
      editing.systemUserFullName = false
      editing.systemUserEmail = false
      const attributes = response?.data?.data?.attributes
      if (attributes?.full_name !== null) values.systemUserFullName = attributes.full_name
      if (attributes?.email !== null) values.systemUserEmail = attributes.email
    }
    if (!archiveInactiveDaysValid.value) return

    const editingSnapshot = { ...editing }
    for (const key of Object.keys(editing)) editing[key] = false

    const settingPayload = {}
    if (editingSnapshot.autoArchiveOnOptOut) settingPayload.auto_archive_on_opt_out = values.autoArchiveOnOptOut
    if (editingSnapshot.autoArchiveInactiveDays)
      settingPayload.auto_archive_inactive_days = values.autoArchiveInactiveDays

    const profilePayload = Object.fromEntries(Object.entries(values).filter(([key]) => !ARCHIVE_KEYS.has(key)))
    if (Object.keys(settingPayload).length) profilePayload.setting_attributes = settingPayload
    if (faviconUpload.value) profilePayload.favicon_attributes = faviconUpload.value

    api.call('PUT', 'profile', { profile: profilePayload })
    faviconUpload.value = null

    if (hasFullAccess.value && paytronix.value) {
      api.call('PUT', 'profile', {
        profile: {
          integrations_attributes: {
            id: paytronix.value.id,
            attribute_one: values.paytronixGroup,
            attribute_two: values.paytronixStore
          }
        }
      })
    }
  }

  const handleDiscard = () => {
    for (const key of Object.keys(editing)) editing[key] = false
    if (hasFullAccess.value) {
      readProfile()
    } else {
      values.email = store.state.loggedInUser.attributes.email
      readSystemUser()
    }
  }

  return {
    values,
    editing,
    officeHoursClosed,
    officeHoursPrevious,
    officeHourDays: OFFICE_HOUR_DAYS,
    faviconUpload,
    faviconPreview,
    financialEnabled,
    paytronix,
    hasUnsavedChanges,
    autoArchiveInactiveEnabled,
    archiveInactiveDaysValid,
    load,
    handleSubmit,
    handleDiscard,
    onOfficeHoursChange,
    toggleOfficeClosed,
    onInactiveToggle,
    onFaviconChange,
    onFaviconRemove
  }
}
